import { createContext, useCallback, useContext, useMemo, useReducer } from 'react'
import * as organizationService from '../services/organizationService'
import {
  OrganizationActionType,
  createInitialOrganizationState,
  organizationReducer,
} from './reducers/organizationReducer'

const OrganizationContext = createContext<any>(null)

export function OrganizationProvider({ children }) {
  const [state, dispatch] = useReducer(
    organizationReducer,
    undefined,
    createInitialOrganizationState,
  )

  const clearError = useCallback(() => {
    dispatch({ type: OrganizationActionType.CLEAR_ERROR })
  }, [])

  const fetchOrganizations = useCallback(async (params: { q?: string; status?: string; missingPhone?: boolean; page?: number; limit?: number } = {}) => {
    dispatch({ type: OrganizationActionType.LOAD_LIST_START })
    try {
      const result = await organizationService.fetchOrganizations(params)
      dispatch({ type: OrganizationActionType.LOAD_LIST_SUCCESS, payload: result })
      return result
    } catch (e) {
      dispatch({
        type: OrganizationActionType.LOAD_LIST_FAILURE,
        payload: e?.message || 'Failed to load organizations',
      })
      throw e
    }
  }, [])

  const getOrganizationById = useCallback(async (id) => {
    dispatch({ type: OrganizationActionType.CLEAR_ERROR })
    try {
      const org = await organizationService.fetchOrganizationById(id)
      dispatch({ type: OrganizationActionType.SET_SELECTED, payload: org })
      return org
    } catch (e) {
      dispatch({
        type: OrganizationActionType.SET_ERROR,
        payload: e?.message || 'Failed to load organization',
      })
      throw e
    }
  }, [])

  const selectOrganization = useCallback(
    async (id) => {
      if (id == null || id === '') {
        dispatch({ type: OrganizationActionType.CLEAR_SELECTED })
        return null
      }
      const fromList = state.organizations.find((o) => o.id === id)
      if (fromList) {
        dispatch({ type: OrganizationActionType.SET_SELECTED, payload: fromList })
        return fromList
      }
      return getOrganizationById(id)
    },
    [state.organizations, getOrganizationById],
  )

  const createOrganization = useCallback(async (payload) => {
    dispatch({ type: OrganizationActionType.MUTATION_START })
    try {
      const created = await organizationService.createOrganization(payload)
      dispatch({ type: OrganizationActionType.CREATE_SUCCESS, payload: created })
      return created
    } catch (e) {
      dispatch({
        type: OrganizationActionType.SET_ERROR,
        payload: e?.message || 'Failed to create organization',
      })
      throw e
    }
  }, [])

  const updateOrganization = useCallback(async (id, payload) => {
    dispatch({ type: OrganizationActionType.MUTATION_START })
    try {
      const updated = await organizationService.updateOrganization(id, payload)
      dispatch({ type: OrganizationActionType.UPDATE_SUCCESS, payload: updated })
      return updated
    } catch (e) {
      dispatch({
        type: OrganizationActionType.SET_ERROR,
        payload: e?.message || 'Failed to update organization',
      })
      throw e
    }
  }, [])

  const deleteOrganization = useCallback(async (id) => {
    dispatch({ type: OrganizationActionType.MUTATION_START })
    try {
      await organizationService.deleteOrganization(id)
      dispatch({ type: OrganizationActionType.DELETE_SUCCESS, payload: id })
    } catch (e) {
      dispatch({
        type: OrganizationActionType.SET_ERROR,
        payload: e?.message || 'Failed to delete organization',
      })
      throw e
    }
  }, [])

  const resendOwnerLoginDetails = useCallback(async (id) => {
    dispatch({ type: OrganizationActionType.MUTATION_START })
    try {
      return await organizationService.resendOwnerLoginDetails(id)
    } catch (e) {
      dispatch({
        type: OrganizationActionType.SET_ERROR,
        payload: e?.message || 'Failed to resend owner login details',
      })
      throw e
    } finally {
      dispatch({ type: OrganizationActionType.MUTATION_END })
    }
  }, [])

  const sendStorefrontWelcome = useCallback(async (id) => {
    dispatch({ type: OrganizationActionType.MUTATION_START })
    try {
      return await organizationService.sendStorefrontWelcome(id)
    } catch (e) {
      dispatch({
        type: OrganizationActionType.SET_ERROR,
        payload: e?.message || 'Failed to send storefront welcome email',
      })
      throw e
    } finally {
      dispatch({ type: OrganizationActionType.MUTATION_END })
    }
  }, [])

  const value = useMemo(
    () => ({
      organizations: state.organizations,
      listTotal: state.listTotal,
      listPage: state.listPage,
      listLimit: state.listLimit,
      listSummary: state.listSummary,
      selectedOrganization: state.selectedOrganization,
      listLoading: state.listLoading,
      mutationLoading: state.mutationLoading,
      loading: state.listLoading || state.mutationLoading,
      error: state.error,
      fetchOrganizations,
      selectOrganization,
      getOrganizationById,
      createOrganization,
      updateOrganization,
      deleteOrganization,
      resendOwnerLoginDetails,
      sendStorefrontWelcome,
      clearError,
    }),
    [
      state.organizations,
      state.listTotal,
      state.listPage,
      state.listLimit,
      state.listSummary,
      state.selectedOrganization,
      state.listLoading,
      state.mutationLoading,
      state.error,
      fetchOrganizations,
      selectOrganization,
      getOrganizationById,
      createOrganization,
      updateOrganization,
      deleteOrganization,
      resendOwnerLoginDetails,
      sendStorefrontWelcome,
      clearError,
    ],
  )

  return (
    <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>
  )
}

export function useOrganization() {
  const ctx = useContext(OrganizationContext)
  if (!ctx) throw new Error('useOrganization must be used within OrganizationProvider')
  return ctx
}
